import React from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import EmptyState from "../../components/ui/EmptyState";
import ViewMaterials from "./ViewMaterials";
import { motion } from "framer-motion";
import { FiBook, FiLayers, FiAward } from "react-icons/fi";

const Materials = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["registeredCoursesMaterials"],
    queryFn: async () => {
      const req = await axiosget(`ms_course`);
      return req?.data;
    },
  });

  return (
    <div>
      <PageTitle title="My Learning Materials" sub="View, download, and read lecture materials" />

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
        </div>
      )}

      {error && <Alert variant="danger"><AlertDescription>Local server error: {error.message}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}

      {data?.type === undefined && data?.length === 0 && (
        <EmptyState
          variant="no-data"
          title="No Materials Found"
          description="No course materials are available at the moment. Check back later."
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
        {data &&
          data?.type === undefined &&
          data?.map((v, i) => (
            <motion.div
              key={`material_${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card padding="none" className="group hover:-translate-y-1 transition-all duration-200 flex flex-col h-full">
                {/* Colored top bar */}
                <div className="h-1.5 bg-gradient-to-r from-primary-500 to-primary-400 rounded-t-lg" />

                <div className="p-5 flex-1 flex flex-col">
                  {/* Course Code + Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                        <FiBook className="w-5 h-5 text-primary-600" />
                      </div>
                      <span className="text-lg font-bold text-neutral-900">{v?.courseinfo?.code}</span>
                    </div>
                    <Badge variant="primary" size="sm">{v?.courseinfo?.credit} Unit{v?.courseinfo?.credit > 1 ? 's' : ''}</Badge>
                  </div>

                  {/* Course Title */}
                  <h3 className="text-sm font-semibold text-neutral-800 mb-4 leading-snug">
                    {v?.courseinfo?.name}
                  </h3>

                  {/* Details */}
                  <div className="space-y-2 mt-auto">
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <FiLayers className="w-3.5 h-3.5" />
                      <span>{v?.courseinfo?.level} | {v?.courseinfo?.semester}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <FiAward className="w-3.5 h-3.5" />
                      <span>{v?.session?.name}</span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-neutral-100 bg-neutral-50/50">
                  <ViewMaterials v={v} />
                </div>
              </Card>
            </motion.div>
          ))}
      </div>
    </div>
  );
};

export default Materials;
